'use client';

import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import Icon from '../Icon';
import Text from '../Text/Text';
import styles from './Comparison.module.scss';

type CardKind = 'text' | 'check' | 'cross';

type ComparisonSectionRow = {
  text: string;
  id: number;
  kind: CardKind;
};

type ComparisonSectionData = {
  title: string;
  description: string;
  cards: {
    id: string;
    isRowTitles: boolean;
    title: string;
    description: string;
    rows: ComparisonSectionRow[];
  }[];
};

const iconStyle = {
  fontSize: '24px',
};

const ComparisonSection = ({ data }: { data: ComparisonSectionData }) => {
  const { cards } = data;
  const nameColumn = cards.find((card) => card.isRowTitles);
  const compareCards = cards.filter((card) => !card.isRowTitles);

  // Refs for measuring row heights
  const leftRowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const compareRowRefs = useRef<(HTMLDivElement | null)[][]>([]);

  const getTableCell = (row: ComparisonSectionRow) => {
    switch (row.kind) {
      case 'text':
        return <p className={styles.columnCompareItemValue}>{row.text}</p>;
      case 'check':
        return (
          <Icon
            name='check'
            style={iconStyle}
            className={styles.columnCompareItemValue}
          />
        );
      case 'cross':
        return (
          <Icon
            name='close'
            style={iconStyle}
            className={styles.columnCompareItemValue}
          />
        );
      default:
        return null;
    }
  };

  // REMOVE THIS AFTER NEW SOLUTION COMES OUT
  // Function to equalize row heights
  const equalizeRowHeights = () => {
    if (!nameColumn?.rows) return;

    const maxRows = Math.max(
      nameColumn.rows.length,
      ...compareCards.map((card) => card.rows?.length || 0),
    );

    for (let rowIndex = 0; rowIndex < maxRows; rowIndex += 1) {
      let maxHeight = 0;

      // Find the maximum height for this row
      const leftRow = leftRowRefs.current[rowIndex];
      if (leftRow) {
        // Reset height to auto to get the natural content height
        leftRow.style.height = 'auto';
        const rowHeight = leftRow.scrollHeight;
        const baseHeight = 56; // min-height from CSS
        const additionalPadding = rowHeight > baseHeight ? 8 : 0;
        maxHeight = Math.max(maxHeight, rowHeight + additionalPadding);
      }

      compareRowRefs.current.forEach((cardRows) => {
        const row = cardRows[rowIndex];
        if (row) {
          // Reset height to auto to get the natural content height
          row.style.height = 'auto';
          const rowHeight = row.scrollHeight;
          const baseHeight = 56; // min-height from CSS
          const additionalPadding = rowHeight > baseHeight ? 8 : 0;
          maxHeight = Math.max(maxHeight, rowHeight + additionalPadding);
        }
      });

      // Set all rows in this position to the maximum height
      if (leftRow) {
        leftRow.style.height = `${maxHeight}px`;
      }

      compareRowRefs.current.forEach((cardRows) => {
        const row = cardRows[rowIndex];
        if (row) {
          row.style.height = `${maxHeight}px`;
        }
      });
    }
  };

  // Effect to equalize heights after render and on window resize
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(equalizeRowHeights, 100);

    const handleResize = () => {
      equalizeRowHeights();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [data]);

  if (!nameColumn) {
    return null;
  }

  const maxRows = Math.max(
    nameColumn.rows?.length || 0,
    ...compareCards.map((card) => card.rows?.length || 0),
  );

  return (
    <div className={styles.comparisonSection}>
      <div className={styles.content}>
        <Text tag='h2'>{data.title}</Text>
        <p className={styles.comparisonDescription}>{data.description}</p>
      </div>
      <div className={styles.comparisonContainer}>
        {nameColumn && (
          <div className={styles.comparisonLeft}>
            <p className={styles.columnTitle}>{nameColumn.title}</p>
            {Array.from({ length: maxRows }, (_, index) => (
              <div
                key={nameColumn.rows?.[index]?.id || index}
                className={styles.row}
                ref={(el) => {
                  leftRowRefs.current[index] = el;
                }}
              >
                <p>{nameColumn.rows?.[index]?.text || ''}</p>
              </div>
            ))}
          </div>
        )}
        <div className={styles.comparisonRight}>
          {compareCards.map((card, cardIndex) => (
            <div
              key={card.id}
              className={clsx(styles.columnCompare)}
            >
              <p className={styles.columnCompareTitle}>
                {card.title}
              </p>
              <p className={styles.columnCompareDescription}>{card.description}</p>
              {Array.from({ length: maxRows }, (_, index) => (card.rows?.[index]
                && (
                <div
                  key={card.rows?.[index]?.id || index}
                  className={styles.columnCompareItem}
                  ref={(el) => {
                    if (!compareRowRefs.current[cardIndex]) {
                      compareRowRefs.current[cardIndex] = [];
                    }
                    compareRowRefs.current[cardIndex][index] = el;
                  }}
                >
                  {getTableCell(card.rows[index])}
                </div>
                )
              ))}
            </div>
          ))}
        </div>
      </div>
      {/* <p className={styles.productComparisonSmallVersion}>{nameColumn.title}</p> */}
      <div className={styles.comparisonSmallVersion}>
        {cards && cards.filter((card) => !card.isRowTitles).map((card) => (
          <div
            key={card.id}
            className={styles.columnCompare}
          >
            <p className={styles.columnCompareTitle}>{card.title}</p>
            <p className={styles.columnCompareDescription}>{card.description}</p>
            {card.rows && card.rows.map((row, indexRow) => (
              <div
                key={row.id}
                className={styles.columnCompareItem}
              >
                <p className={styles.columnCompareItemTitle}>{nameColumn.rows[indexRow]?.text}</p>
                {getTableCell(row)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSection;
